/**
 * @fileoverview SessionGrid Component
 * 
 * Displays a responsive grid of active testing sessions in the Virtual Test Center.
 * Automatically calculates optimal layout dimensions based on the number of sessions
 * and available container space, similar to Zoom's gallery view.
 * 
 * @module components/SessionGrid
 */

import { Flex, Text, View, } from '@smarterservices/smarter-ui';
import { motion } from 'framer-motion';
import { debounce } from '@instructure/debounce';
import useSessionStore from '../../stores/session';
import useLayoutStore from '../../stores/layout';
import SessionCard from '../SessionCard';
import { useEffect, useMemo, useState, useRef, memo } from 'react';

/**
 * SessionCardWrapper Component
 * 
 * Wrapper component that allows Flex.Item dimensions to update while
 * preventing SessionCard from re-rendering. Uses useMemo to memoize
 * the SessionCard instance so it doesn't re-render when only the
 * Flex.Item dimensions change.
 * 
 * This optimization is crucial for performance when the drawer opens/closes
 * or the window resizes, as it prevents all SessionCards from re-rendering
 * when only their container dimensions change.
 */
const SessionCardWrapper = ({ sessionSid, width, height }) => {
  // Memoize the SessionCard instance - only recreate when sessionSid changes
  const memoizedCard = useMemo(() => (
    <SessionCard sessionSid={sessionSid} />
  ), [sessionSid]);

  return (
    <motion.div
      layoutId={`session-card-${sessionSid}`}
      layout
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{
        opacity: 1,
        scale: 1,
        width: width,
        height: height
      }}
      exit={{ opacity: 0, scale: 0.98 }}
      transition={{
        layout: {
          type: "tween",
          duration: 0.3,
          ease: "easeInOut"
        },
        width: {
          type: "tween",
          duration: 0.3,
          ease: "easeInOut"
        },
        height: {
          type: "tween",
          duration: 0.3,
          ease: "easeInOut"
        },
        opacity: { duration: 0.3 },
        scale: { duration: 0.3 }
      }}
      style={{
        padding: '4px'
      }}
    >
      {memoizedCard}
    </motion.div>
  );
};

/**
 * SessionGrid Component
 * 
 * Renders a responsive grid layout of active testing sessions. The grid automatically
 * adjusts the size and arrangement of session cards to maximize video visibility while
 * maintaining aspect ratios. Uses CSS Flexbox with wrapping to create a Zoom-like
 * gallery view experience.
 * 
 * **Features:**
 * - Responsive grid layout that adapts to container size
 * - Automatic dimension calculation for optimal video display
 * - Memoized rendering to prevent unnecessary re-renders
 * - Empty state message when no sessions are active
 * - Integrates with layout store for dynamic sizing
 * 
 * **Layout Behavior:**
 * - Sessions are arranged in rows using Flexbox with wrapping
 * - Each session card receives calculated width/height from the layout store
 * - Dimensions are recalculated when the number of sessions changes
 * - Cards are centered within the available space
 * 
 * **Performance Optimizations:**
 * - Session entries are memoized to prevent array recreation
 * - Session cards are memoized based on entries and dimensions
 * - Layout calculations are triggered only when session count changes
 * 
 * **Store Dependencies:**
 * - `useSessionStore`: Provides testing sessions and count
 * - `useLayoutStore`: Provides dimension calculations and sizing logic
 * 
 * @component
 * @returns {JSX.Element} The rendered session grid or empty state message
 * 
 * @example
 * // Basic usage in VirtualTestCenter
 * <SessionGrid />
 * 
 * @example
 * // The grid automatically handles different session counts:
 * // - 1 session: Large single view
 * // - 2-4 sessions: 2x2 grid
 * // - 5-9 sessions: 3x3 grid
 * // - 10+ sessions: Dynamically calculated grid
 */
const SessionGrid = () => {
  // ============================================================================
  // STORE HOOKS
  // ============================================================================

  /**
   * Layout store provides:
   * - debouncedLayoutUpdate: Debounced function to recalculate grid dimensions
   * - sessionDimensions: Current calculated width/height for each session card
   */
  const debouncedLayoutUpdate = useLayoutStore((state) => state.debouncedLayoutUpdate);
  //const sessionDimensions = useLayoutStore((state) => state.sessionDimensions);

  /**
   * Session store provides:
   * - visibleSessions: Array of sessions currently visible in the grid
   * - focusedSessionSid: Session ID of the currently focused session
   */
  const visibleSessions = useSessionStore((state) => state.visibleSessions);
  const focusedSessionSid = useSessionStore((state) => state.focusedSessionSid);

  const finalVisibleSessions = useMemo(() => {
    return visibleSessions.filter((session) => session.sessionSid !== focusedSessionSid);
  }, [visibleSessions, focusedSessionSid]);

  const [sessionDimensions, setSessionDimensions] = useState({
    width: 0,
    height: 0,
  });

  const [roomDimensions, setRoomDimensions] = useState({
    width: 0,
    height: 0,
  });

  // Ref for the container element
  const containerRef = useRef(null);

  // Ref for debounced resize function
  const debouncedResizeRef = useRef(null);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  /**
   * Set up ResizeObserver to track container dimensions with debouncing
   */
  useEffect(() => {
    if (!containerRef.current) return;

    // Create debounced resize handler (50ms)
    debouncedResizeRef.current = debounce((width, height) => {
      setRoomDimensions({ width, height });
    }, 50);

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        debouncedResizeRef.current(width, height);
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  /**
   * Recalculate layout dimensions when room dimensions or sessions change.
   */
  useEffect(() => {
    if (roomDimensions.width === 0 || roomDimensions.height === 0) return;

    if (finalVisibleSessions.length > 0) {
      const sessionSize = useLayoutStore.getState().calculateSessionSize(
        roomDimensions.width,
        roomDimensions.height,
        finalVisibleSessions.length
      );

      setSessionDimensions(sessionSize);
    }
  }, [finalVisibleSessions, roomDimensions]);

  // ============================================================================
  // MEMOIZED VALUES
  // ============================================================================

  /**
   * Render all session cards with calculated dimensions.
   * Each card is wrapped in SessionCardWrapper which uses custom memoization
   * to prevent SessionCard re-renders when only dimensions change.
   * 
   * **Performance Optimization:**
   * SessionCardWrapper uses a custom comparison function that only triggers
   * re-renders when sessionSid changes, ignoring width/height changes.
   * This prevents unnecessary re-renders when:
   * - Drawer opens/closes (dimension changes)
   * - Window resizes (dimension changes)
   * - Container size adjusts (dimension changes)
   * 
   * **Important:** Filters out the focused session to prevent React reconciliation errors.
   * When a session transitions to focus mode, we must not render its SessionCard at all,
   * otherwise React will try to reconcile the SessionViewerPortal in both locations
   * simultaneously, causing DOM insertion errors.
   * 
   * Dependencies:
   * - visibleSessions: Triggers re-render when sessions are added/removed or order changes
   * - focusedSessionSid: Triggers re-render when focus state changes
   * - sessionDimensions: Triggers re-render of wrappers (but not SessionCards)
   */
  const sessionCards = useMemo(() => {
    return finalVisibleSessions.map((session) => (
      <SessionCardWrapper
        key={`session_${session.sessionSid}`}
        sessionSid={session.sessionSid}
        width={sessionDimensions?.width}
        height={sessionDimensions?.height}
      />
    ));
  }, [finalVisibleSessions, sessionDimensions]);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <View
      as="div"
      width="100%"
      height="100%"
      elementRef={(el) => containerRef.current = el}
    >
      {finalVisibleSessions.length > 0 ? (
        // Visible sessions: Render grid layout
        <Flex
          as="div"
          direction="row"
          justifyItems="center"
          alignItems="center"
          margin="none"
          withVisualDebug={false}
          padding="none"
          gap="none"
          wrap="wrap"
        >
          {sessionCards}
        </Flex>
      ) : (
        // Empty state: Show informational message

        <Flex
          as="div"
          direction="column"
          justifyItems="center"
          alignItems="center"
          margin="none"
          padding="none"
          gap="small"
          height="100%">

          <Text variant="info" margin="none medium">
            No Sessions Currently Testing
          </Text>
        </Flex >
      )}
    </View>
  );
};

export default SessionGrid;
